import { mockCallHistoryData } from "../mock/mockData";
import grandstreamClient from "./grandstreamClient";
import { PBX_MSSQL_CALL_HISTORY, PBX_MSSQL_EMPLOYEE_STATS, PBX_MSSQL_SYNC_STATUS, PBX_MSSQL_SYNC_NOW, PBX_MY_EXTENSION, PBX_REC_BASE_URL } from "../../utils/ApiRoute";

/**
 * Service to manage Call History (CDR) records backed by MSSQL database.
 */
export const callHistoryService = {
  getCallHistory: async (filters = {}) => {
    // Demo Mode Check
    if (localStorage.getItem('pbx_demo_mode') === 'true') {
      let data = [...mockCallHistoryData.response];
      if (filters.search) {
        const q = filters.search.toLowerCase();
        data = data.filter((c) => c.callId.toLowerCase().includes(q) || c.caller.toLowerCase().includes(q) || c.callee.toLowerCase().includes(q));
      }
      return Promise.resolve({ status: 0, response: data, totalItem: data.length, totalPage: 1 });
    }

    const pageNum = filters.page || 1;
    const itemNum = filters.item_num || 50;

    // 1. Try MSSQL Backend API Endpoint
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', pageNum);
      queryParams.append('item_num', itemNum);

      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status && filters.status !== 'All') queryParams.append('disposition', filters.status);

      const headers = { 'Content-Type': 'application/json' };
      const authToken = localStorage.getItem("AuthToken") || localStorage.getItem("token");
      if (authToken) {
        try {
          const bearer = JSON.parse(authToken)?.token || JSON.parse(authToken);
          headers['Authorization'] = `Bearer ${bearer}`;
        } catch (e) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
      }

      const res = await fetch(`${PBX_MSSQL_CALL_HISTORY}?${queryParams.toString()}`, {
        method: 'GET',
        headers: headers
      });

      if (res.ok) {
        const data = await res.json();
        if (data && (data.status === 0 || data.status === 200)) {
          const rawList = data.response || [];

          const formattedCdr = rawList.map((item, idx) => {
            const billsec = parseInt(item.billsec || 0, 10);
            const durationSec = parseInt(item.duration || billsec, 10);

            let statusStr = "Failed";
            const disp = String(item.disposition || "").toUpperCase();
            if (disp === "ANSWERED") statusStr = "Answered";
            else if (disp === "NO ANSWER" || disp === "NOANSWER") statusStr = "Missed";
            else if (disp === "BUSY") statusStr = "Busy";

            let dir = "Inbound";
            const userfield = String(item.userfield || "").toLowerCase();
            if (userfield.includes("outbound") || item.dst_trunk_name) dir = "Outbound";
            else if (userfield.includes("internal")) dir = "Internal";

            const callerDisplay = item.caller_name ? `${item.caller_name.trim()} (${item.src})` : item.caller || item.src || "Unknown";
            const calleeDisplay = item.callee_name ? `${item.callee_name.trim()} (${item.dst})` : item.callee || item.dst || "Unknown";

            return {
              callId: item.acctid || item.callId || `CDR-${idx + 1}`,
              caller: callerDisplay,
              callerNum: item.src || "-",
              callee: calleeDisplay,
              extension: item.src || "-",
              direction: dir,
              startTime: item.startTime || item.start || "-",
              endTime: item.endTime || item.end || "-",
              duration: `${Math.floor(durationSec / 60)}m ${durationSec % 60}s`,
              durationSec: durationSec,
              billsec: billsec,
              status: statusStr,
              trunk: item.trunk || "Trunk_SIP_Primary",
              recordingUrl: item.recordfiles ? `${PBX_REC_BASE_URL}/${item.recordfiles}` : null
            };
          });

          const totalItem = data.total || formattedCdr.length;
          const totalPage = Math.ceil(totalItem / itemNum) || 1;

          return {
            status: 0,
            response: formattedCdr,
            totalItem: totalItem,
            totalPage: totalPage,
            page: pageNum
          };
        }
      }
    } catch (mssqlErr) {
      console.warn("MSSQL Call History query notice, falling back to direct PBX API:", mssqlErr.message);
    }

    // 2. Direct PBX API Fallback
    const pad = (n) => String(n).padStart(2, '0');
    const now = new Date();
    const past = new Date();
    past.setDate(now.getDate());

    const defaultStartFull = `${past.getFullYear()}-${pad(past.getMonth() + 1)}-${pad(past.getDate())} 00:00:00`;
    const defaultEndFull = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} 23:59:59`;

    const cleanDateOnly = (dStr) => {
      if (!dStr) return '';
      const trimmed = dStr.trim();
      if (trimmed.includes(' ')) return trimmed.split(' ')[0];
      if (trimmed.includes('T')) return trimmed.split('T')[0];
      return trimmed;
    };

    const startDateVal = (filters.startDate && filters.startDate.trim() !== '') ? cleanDateOnly(filters.startDate) : cleanDateOnly(defaultStartFull);
    const endDateVal = (filters.endDate && filters.endDate.trim() !== '') ? cleanDateOnly(filters.endDate) : cleanDateOnly(defaultEndFull);

    const cdrParams = {
      page: pageNum,
      item_num: itemNum,
      startTime: `${startDateVal}T00:00:00`,
      endTime: `${endDateVal}T23:59:59`,
      skey: "acctid",
      sdir: "desc",
      format: "json"
    };

    const liveCdr = await grandstreamClient.sendAction("cdrapi", cdrParams);
    const rawList = liveCdr?.response?.acctid || liveCdr?.response || [];

    return {
      status: 0,
      response: Array.isArray(rawList) ? rawList : [],
      totalItem: liveCdr?.total_item || 0,
      totalPage: liveCdr?.total_page || 1,
      page: pageNum
    };
  },

  getCallDetail: async (callId) => {
    const historyRes = await callHistoryService.getCallHistory();
    const callItem = historyRes.response.find((c) => c.callId === callId);
    return Promise.resolve({
      status: 0,
      response: callItem || null
    });
  },

  getSyncStatus: async () => {
    try {
      const res = await fetch(PBX_MSSQL_SYNC_STATUS);
      if (res.ok) return await res.json();
    } catch (e) {
      console.warn("Could not fetch PBX sync status:", e.message);
    }
    return { status: -1, last_sync: null };
  },

  triggerManualSync: async () => {
    try {
      const res = await fetch(PBX_MSSQL_SYNC_NOW, { method: 'POST' });
      if (res.ok) return await res.json();
    } catch (e) {
      console.error("Error triggering manual sync:", e.message);
    }
    return { status: -1 };
  },

  getEmployeeCallStats: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.extension) queryParams.append('extension', filters.extension);
      if (filters.employee_name) queryParams.append('employee_name', filters.employee_name);
      if (filters.limit) queryParams.append('limit', filters.limit);

      const res = await fetch(`${PBX_MSSQL_EMPLOYEE_STATS}?${queryParams.toString()}`);
      if (res.ok) return await res.json();
    } catch (e) {
      console.error("Error fetching PBX employee call stats:", e.message);
    }
    return {
      status: -1,
      total_calls: 0,
      answered_calls: 0,
      missed_calls: 0,
      total_duration_sec: 0,
      total_duration_formatted: "0m 0s",
      recent_calls: []
    };
  },

  getMyExtension: async (empId) => {
    try {
      const res = await fetch(`${PBX_MY_EXTENSION}?emp_id=${empId}`);
      if (res.ok) return await res.json();
    } catch (e) {
      console.error("Error fetching employee extension:", e.message);
    }
    return { status: -1, extension: "" };
  },

  checkMyExtension: async (extension) => {
    try {
      const res = await fetch(`${PBX_MY_EXTENSION}?check_ext=${encodeURIComponent(extension)}`);
      const data = await res.json();
      return data;
    } catch (e) {
      console.error("Error checking extension:", e.message);
    }
    return { status: -1, error: "Failed to connect to server" };
  },

  saveMyExtension: async (empId, extension, employeeName = "", action = "save") => {
    try {
      const res = await fetch(PBX_MY_EXTENSION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emp_id: empId, extension: extension, employee_name: employeeName, action: action })
      });
      const data = await res.json();
      return data;
    } catch (e) {
      console.error("Error saving employee extension:", e.message);
    }
    return { status: -1, error: "Failed to connect to server" };
  }
};

export default callHistoryService;
